import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { cartService, type ShippingAddressPayload } from '../services/cart.service'
import { useAuth } from '@/providers/auth-provider'

export const cartKeys = {
  cart: ['cart'] as const,
  orders: ['orders'] as const,
}

export function useCart() {
  const { isAuthenticated } = useAuth()

  return useQuery({
    queryKey: cartKeys.cart,
    queryFn: async () => {
      const res = await cartService.getCart()
      return res.data ?? { items: [], totalCount: 0, totalAmount: 0 }
    },
    enabled: Boolean(isAuthenticated),
  })
}

export function useAddToCart() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ partId, quantity = 1 }: { partId: number; quantity?: number }) =>
      cartService.addToCart(partId, quantity),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cartKeys.cart })
    },
  })
}

export function useUpdateCartQuantity() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, quantity }: { id: number; quantity: number }) =>
      cartService.updateQuantity(id, quantity),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cartKeys.cart })
    },
  })
}

export function useRemoveCartItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => cartService.removeItem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cartKeys.cart })
    },
  })
}

export function useCheckout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ shippingAddress, paymentMethod }: { shippingAddress: ShippingAddressPayload; paymentMethod?: string }) =>
      cartService.checkout(shippingAddress, paymentMethod),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cartKeys.cart })
      queryClient.invalidateQueries({ queryKey: cartKeys.orders })
    },
  })
}

export function useMyOrders() {
  const { isAuthenticated } = useAuth()
  return useQuery({
    queryKey: cartKeys.orders,
    queryFn: async () => {
      const res = await cartService.getMyOrders()
      return res.data ?? []
    },
    enabled: Boolean(isAuthenticated),
  })
}

export function useAdminOrders() {
  return useQuery({
    queryKey: [...cartKeys.orders, 'admin-all'],
    queryFn: async () => {
      const res = await cartService.getAllOrders()
      return res.data ?? []
    },
  })
}

export function useUpdateOrderStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, status, trackingNumber }: { id: number; status: string; trackingNumber?: string }) =>
      cartService.updateOrderStatus(id, status, trackingNumber),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cartKeys.orders })
    },
  })
}
